import { preferencesProcessingAllowed, subscribeToPreferences } from "@theme/customer-privacy";

/** @constant {string} The key used to store the viewed products in local storage */
const STORAGE_KEY = "viewedProducts";
/** @constant {number} The maximum number of products to store */
const MAX_PRODUCTS = 4;

/** @type {RecentlyViewedProduct | null} Only the activated product page, never a pre-consent history queue. */
let currentProduct = null;

/**
 * @typedef {object} RecentlyViewedProduct
 * @property {string} id - The Shopify product ID.
 * @property {string} handle - The canonical Shopify product handle.
 */

/**
 * Normalize and validate a recently viewed product before it reaches storage or a Liquid lookup.
 * Numeric-ID-only history from the previous format is intentionally rejected: storefront search
 * was not an exact ID resolver and could return a different product.
 * @param {unknown} product - The product record to validate.
 * @returns {RecentlyViewedProduct | null} A safe product record, or null when invalid.
 */
function normalizeProduct(product) {
  if (!product || typeof product !== "object" || Array.isArray(product)) {
    return null;
  }

  const { id, handle } = /** @type {{ id?: unknown; handle?: unknown }} */ (product);
  if (typeof id !== "string" || !/^\d+$/.test(id) || typeof handle !== "string") {
    return null;
  }

  const normalizedHandle = handle.trim().toLowerCase();
  if (!/^[a-z0-9][a-z0-9-]*$/.test(normalizedHandle)) {
    return null;
  }

  return { id, handle: normalizedHandle };
}

/**
 * Updates the recently viewed products in localStorage.
 */
export const RecentlyViewed = {
  /**
   * Records a product view once its document represents an activated navigation.
   * Speculative prerenders execute page scripts before the shopper visits the page,
   * so defer their storage write until the browser activates that prerender.
   * @param {string} productId - The ID of the product being viewed.
   * @param {string} productHandle - The canonical handle of the product being viewed.
   */
  trackProductView(productId, productHandle) {
    const currentDocument = /** @type {Document & { prerendering?: boolean }} */ (document);
    const recordActivatedView = () => {
      currentProduct = normalizeProduct({ id: productId, handle: productHandle });
      RecentlyViewed.addProduct(productId, productHandle);
    };

    if (currentDocument.prerendering) {
      currentDocument.addEventListener("prerenderingchange", recordActivatedView, {
        once: true,
      });
      return;
    }

    recordActivatedView();
  },

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   * @param {string} productHandle - The canonical handle of the product to add.
   */
  addProduct(productId, productHandle) {
    const allowed = preferencesProcessingAllowed();
    if (allowed !== true) {
      if (allowed === false) {
        RecentlyViewed.clearProducts();
      }
      return;
    }

    const product = normalizeProduct({ id: productId, handle: productHandle });
    if (!product) {
      return;
    }

    const viewedProducts = RecentlyViewed.getEntries().filter(entry => entry.id !== product.id);
    viewedProducts.unshift(product);

    const limitedProducts = viewedProducts.slice(0, MAX_PRODUCTS);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(limitedProducts));
    } catch {
      // Storage write may fail (quota exceeded, private-mode); fail gracefully.
    }
  },

  clearProducts() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage access can be denied; the search surface should remain usable.
    }
  },

  /**
   * Retrieves exact product records from local storage.
   * @returns {RecentlyViewedProduct[]} The validated product records, newest first.
   */
  getEntries() {
    const allowed = preferencesProcessingAllowed();
    if (allowed !== true) {
      if (allowed === false) {
        RecentlyViewed.clearProducts();
      }
      return [];
    }

    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      if (!Array.isArray(parsed)) {
        return [];
      }

      const products = parsed.map(product => normalizeProduct(product));
      if (products.includes(null)) {
        return [];
      }

      return /** @type {RecentlyViewedProduct[]} */ (products).slice(0, MAX_PRODUCTS);
    } catch {
      return [];
    }
  },

  /**
   * Retrieves the list of recently viewed products from local storage.
   * @returns {string[]} The list of viewed products.
   */
  getProducts() {
    return RecentlyViewed.getEntries().map(product => product.id);
  },
};

subscribeToPreferences(allowed => {
  if (allowed === false) {
    RecentlyViewed.clearProducts();
  } else if (allowed && currentProduct) {
    RecentlyViewed.addProduct(currentProduct.id, currentProduct.handle);
  }
});
